/**
 * QXAI Services Wrapper - Dashboard Compatible
 * 
 * This wrapper translates between Dashboard's parameter expectations
 * and apiService.js's actual implementation.
 * 
 * Backend: https://vtaas-backend-220419304601.us-central1.run.app
 * Frontend: https://quantumxai.web.app
 */

import api from './apiService';

// ============================================================================
// WRAPPER FUNCTIONS - Match Dashboard's Expected Signatures
// ============================================================================

/**
 * Generate chat response (text generation)
 * 
 * Dashboard calls: generateChat(userHash, message, model)
 * apiService expects: simpleChat(message, model)
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored, apiService gets it from localStorage)
 * @param {string} message - User's message
 * @param {string} model - AI model to use
 * @returns {Promise<string>} - Plain text response
 */
export const generateChat = async (userHash, message, model = 'gemini-2.5-pro') => {
  try {
    // Call apiService (it gets hash from localStorage automatically)
    const result = await api.simpleChat(message, model);
    
    // Dashboard expects a plain string response
    // Extract from various possible response formats
    if (typeof result === 'string') {
      return result;
    }
    
    if (result?.response) {
      return result.response;
    }
    
    if (result?.text) {
      return result.text;
    }
    
    if (result?.content) {
      return result.content;
    }
    
    // Fallback: stringify the result
    return String(result);
    
  } catch (error) {
    console.error('❌ generateChat error:', error);
    throw new Error(error.message || 'Failed to generate chat response');
  }
};

/**
 * Generate image
 * 
 * Dashboard calls: generateImage(userHash, prompt, model)
 * apiService expects: generateImage(prompt, options)
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored)
 * @param {string} prompt - Image generation prompt
 * @param {string} model - Image model to use
 * @returns {Promise<object>} - { success, image_url, model, credits_used }
 */
export const generateImage = async (userHash, prompt, model = 'flux-schnell') => {
  try {
    // Call apiService with proper options format
    const result = await api.generateImage(prompt, {
      model: model,
      width: 1024,
      height: 1024
    });
    
    // Ensure consistent response format for Dashboard
    return {
      success: result.success !== false,
      image_url: result.image_url || result.url || result.imageUrl,
      model: result.model || model,
      credits_used: result.credits_used || result.creditsUsed || 15,
      ...result // Include any other properties
    };
    
  } catch (error) {
    console.error('❌ generateImage error:', error);
    throw new Error(error.message || 'Failed to generate image');
  }
};

/**
 * Generate video
 * 
 * Dashboard calls: generateVideo(userHash, prompt, model)
 * apiService expects: generateVideo(prompt, options)
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored)
 * @param {string} prompt - Video generation prompt
 * @param {string} model - Video model to use
 * @returns {Promise<object>} - { success, video_url, model, credits_used }
 */
export const generateVideo = async (userHash, prompt, model = 'kling-1.0') => {
  try {
    const result = await api.generateVideo(prompt, {
      model: model,
      duration: 5
    });
    
    return {
      success: result.success !== false,
      video_url: result.video_url || result.url || result.videoUrl,
      model: result.model || model,
      credits_used: result.credits_used || result.creditsUsed || 50,
      ...result
    };
    
  } catch (error) {
    console.error('❌ generateVideo error:', error);
    throw new Error(error.message || 'Failed to generate video');
  }
};

/**
 * Get user credits
 * 
 * Dashboard calls: getUserCredits(userHash)
 * apiService expects: getUserCredits()
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored)
 * @returns {Promise<number>} - Number of credits available
 */
export const getUserCredits = async (userHash) => {
  try {
    const credits = await api.getUserCredits();
    
    // Handle different response formats
    if (typeof credits === 'number') {
      return credits;
    }
    
    if (credits?.credits !== undefined) {
      return credits.credits;
    }
    
    if (credits?.available !== undefined) {
      return credits.available;
    }
    
    // Default fallback
    return 100;
    
  } catch (error) {
    console.error('❌ getUserCredits error:', error);
    // Return default instead of throwing - don't break the UI
    return 100;
  }
};

/**
 * Get user information
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored)
 * @returns {Promise<object>} - User information
 */
export const getUserInfo = async (userHash) => {
  try {
    const userInfo = await api.getUserInfo();
    return userInfo;
  } catch (error) {
    console.error('❌ getUserInfo error:', error);
    throw new Error(error.message || 'Failed to get user info');
  }
};

/**
 * Transcribe audio
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored)
 * @param {File|Blob} audioFile - Audio file to transcribe
 * @returns {Promise<object>} - { success, text, ... }
 */
export const transcribeAudio = async (userHash, audioFile) => {
  try {
    const result = await api.transcribeAudio(audioFile);
    return result;
  } catch (error) {
    console.error('❌ transcribeAudio error:', error);
    throw new Error(error.message || 'Failed to transcribe audio');
  }
};

/**
 * Generate audio (text-to-speech)
 * 
 * @param {string} userHash - User's SHA-256 hash (ignored)
 * @param {string} text - Text to convert to speech
 * @param {object} options - TTS options
 * @returns {Promise<object>} - { success, audio_url, ... }
 */
export const generateAudio = async (userHash, text, options = {}) => {
  try {
    const result = await api.generateAudio(text, options);
    return result;
  } catch (error) {
    console.error('❌ generateAudio error:', error);
    throw new Error(error.message || 'Failed to generate audio');
  }
};

// ============================================================================
// DIRECT API ACCESS (for advanced usage)
// ============================================================================

/**
 * Export the raw API service for direct access
 * Use this when you need features not wrapped above
 */
export const rawApi = api;

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

/**
 * Export common patterns
 */
export default {
  // Core functions Dashboard uses
  generateChat,
  generateImage,
  generateVideo,
  getUserCredits,
  getUserInfo,
  
  // Additional features
  transcribeAudio,
  generateAudio,
  
  // Raw API access
  api: rawApi,
};

/**
 * USAGE IN DASHBOARD:
 * 
 * import { generateChat, generateImage, getUserCredits } from '../../services';
 * 
 * // Text generation
 * const response = await generateChat(userHash, "Hello!", "gemini-2.5-pro");
 * 
 * // Image generation  
 * const result = await generateImage(userHash, "A cat", "flux-schnell");
 * 
 * // Get credits
 * const credits = await getUserCredits(userHash);
 */